import { useState } from "react";
import { faFilter } from "@fortawesome/pro-regular-svg-icons";

import { IPaginator } from "common/types";
import { Optional } from "common/utils/types";
import Icon from "common/components/Icon";
import Spinner from "common/components/Spinner";

import { ProductListFilterSurface } from "client/analytics/types";
import PillButton from "client/common/components/buttons/PillButton";

import {
  FilterChangeHandler,
  FilterDefinition,
  FilterValue,
  ProductSearchFilters,
  SelectedProductFilters,
} from "../types";
import { getSearchString } from "../utils";
import AppliedFilterTags from "./AppliedFilterTags";
import FilterPanelModal from "./FilterPanelModal";

import styles from "../styles/ProductCardListHeader.module.less";

type ProductCardListHeaderProps = {
  paginator: IPaginator;
  isRefetching: boolean;
  /** Pluralized in the count, e.g. "deal" reads "Showing 1 - 14 of 14 deals". */
  entityName: string;
  definitions: FilterDefinition<SelectedProductFilters, FilterValue, ProductSearchFilters>[];
  filterOptions: Optional<ProductSearchFilters>;
  filters: SelectedProductFilters;
  changeFrom: (
    surface: ProductListFilterSurface,
  ) => FilterChangeHandler<SelectedProductFilters, ProductSearchFilters>;
  /** Whether the page offers filtering; false renders the result count alone. */
  showFilters?: boolean;
};

type FilterActionsProps = Pick<
  ProductCardListHeaderProps,
  "definitions" | "filterOptions" | "filters" | "changeFrom"
>;

const FilterActions = ({ definitions, filterOptions, filters, changeFrom }: FilterActionsProps) => {
  const [isFiltering, setIsFiltering] = useState(false);

  return (
    <div className={styles.actions}>
      {filterOptions && (
        <AppliedFilterTags<SelectedProductFilters, ProductSearchFilters>
          definitions={definitions}
          options={filterOptions}
          value={filters}
          onChange={changeFrom(ProductListFilterSurface.AppliedFilters)}
        />
      )}
      <PillButton
        variant="secondary"
        size="s"
        type="button"
        onClick={() => setIsFiltering(true)}
        disabled={!filterOptions}
      >
        <span className={styles.filter}>
          <Icon icon={faFilter} /> Filter
        </span>
      </PillButton>
      {filterOptions && (
        <FilterPanelModal
          definitions={definitions}
          options={filterOptions}
          isVisible={isFiltering}
          onClose={() => setIsFiltering(false)}
          value={filters}
          onChange={changeFrom(ProductListFilterSurface.Drawer)}
        />
      )}
    </div>
  );
};

/** The bar above a ProductCardList: result count, active filter chips, and the filter modal. */
const ProductCardListHeader = ({
  paginator,
  isRefetching,
  entityName,
  definitions,
  filterOptions,
  filters,
  changeFrom,
  showFilters = true,
}: ProductCardListHeaderProps) => (
  <div className={styles.header}>
    <div className={styles.label}>
      {isRefetching ? <Spinner /> : getSearchString(paginator, entityName)}
    </div>
    {showFilters && (
      <FilterActions
        definitions={definitions}
        filterOptions={filterOptions}
        filters={filters}
        changeFrom={changeFrom}
      />
    )}
  </div>
);

export default ProductCardListHeader;
